import type { Scene, LandmarkData, SpeciesData, MapAdapter } from '../types';
import type { MapLibreAdapter } from '../adapters/MapLibreAdapter';
import { RadialChart } from '../components/RadialChart';

const TITILER = 'https://titiler-centinela-746476722093.us-central1.run.app';
const COG_URI = 'gs://centinela-test-data/openclaw/embeddings/embedding_mopane_baresoil_rgb_cog.tif';
const TILE_URL =
  `${TITILER}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}` +
  `?url=${encodeURIComponent(COG_URI)}` +
  `&bidx=2&bidx=1&bidx=3` +
  `&rescale=-0.02%2C0.22&rescale=-0.15%2C0.20&rescale=-0.10%2C0.18` +
  `&nodata=nan`;
const COG_LAYER_ID = 'cog-rgb';

// Diff chart colors
const COL_SOIL   = 'hsl(42,100%,62%)';   // gold  → soil higher
const COL_MOPANE = 'hsl(185,100%,62%)';  // teal  → mopane higher

export class SceneSoilVsMopane implements Scene {
  readonly panel: HTMLElement;
  private diffRadial!: RadialChart;
  private built = false;
  private mapLegend: HTMLElement | null = null;

  private readonly diff:   number[];
  private readonly cosSim: number;

  constructor(
    root: HTMLElement,
    private readonly soil:   LandmarkData,
    private readonly mopane: SpeciesData,
    private readonly map:    MapAdapter,
  ) {
    this.diff   = soil.embed.map((v, i) => v - mopane.embed[i]);
    this.cosSim = soil.embed.reduce((s, v, i) => s + v * mopane.embed[i], 0);

    const topDims = this.diff
      .map((v, i) => ({ i, v, abs: Math.abs(v) }))
      .sort((a, b) => b.abs - a.abs)
      .slice(0, 8);

    // Color each of the three visualized bands by their diff direction
    const bandColor = (idx: number) => this.diff[idx] > 0 ? COL_SOIL : COL_MOPANE;
    const a01col = bandColor(1);
    const a44col = bandColor(44);
    const a25col = bandColor(25);

    this.panel = document.createElement('div');
    this.panel.className = 'sm-panel';
    this.panel.dataset['scene'] = 'soilvsmopane';

    this.panel.innerHTML = `
      <div class="sm-panel-header">
        <div class="sm-panel-title">Bare Soil vs Mopane Forest</div>
        <div class="sm-panel-sub">
          AlphaEarth 2024 · embedding signal comparison · hover a vertex to inspect
        </div>
      </div>

      <div class="sm-panel-narrative">
        The foundation model produces a clear, distinct signal between bare escarpment
        soil and Mopane forest — two of the most ecologically important classes in this
        landscape. This separation lets us map habitat likelihood across the entire
        concession and track for class changes over time.
      </div>

      <div class="sm-headline">
        A clear, distinct signal separates bare escarpment soil from Mopane forest —
        enough to map habitat likelihood across the entire concession and
        track class changes over time without repeated field campaigns.
      </div>

      <div class="sm-svm-legend">
        <span class="sm-svm-legend-item" style="color:${COL_SOIL};">
          &#9650; outward — escarpment soil higher
        </span>
        <span class="sm-svm-legend-item" style="color:${COL_MOPANE};">
          &#9660; inward — mopane higher
        </span>
      </div>

      <div class="sm-svm-chart-wrap" id="sm-svm-diff-wrap"></div>

      <div class="sm-svm-dims-row" id="sm-svm-dims">
        ${topDims.map(d => {
          const col   = d.v > 0 ? COL_SOIL : COL_MOPANE;
          const arrow = d.v > 0 ? '&#9650;' : '&#9660;';
          const who   = d.v > 0 ? 'soil' : 'mopane';
          const barW  = Math.round((d.abs / (topDims[0].abs || 1)) * 100);
          return `
            <div class="sm-svm-dim-card">
              <div class="sm-svm-dim-band" style="color:${col};">${arrow} A${String(d.i).padStart(2,'0')}</div>
              <div class="sm-svm-dim-bar-track">
                <div class="sm-svm-dim-bar-fill" style="width:${barW}%;background:${col};"></div>
              </div>
              <div class="sm-svm-dim-who">${who}</div>
              <div class="sm-svm-dim-val">${d.v > 0 ? '+' : ''}${d.v.toFixed(3)}</div>
            </div>`;
        }).join('')}
      </div>

      <div class="sm-svm-rgb-section">
        <div class="sm-section-label" style="padding:12px 22px 4px;">
          Landscape Distribution · Three Signals Applied
        </div>
        <div class="sm-svm-rgb-text">
          We test the most distinct signals —
          <span style="color:${a01col};">A01</span>,
          <span style="color:${a44col};">A44</span>,
          <span style="color:${a25col};">A25</span>
          — and apply them across the landscape to check habitat distribution
          through the lens of the model.
        </div>
        <div class="sm-svm-rgb-text">
          Where the human eye only interprets satellite data from red, green and blue,
          deep learning produces multidimensional fingerprints called embeddings &mdash;
          each 64-dimensional vector encodes the full spectrum of the landscape&rsquo;s
          spectral, structural, and temporal character.
        </div>
        <div class="sm-svm-rgb-text">
          When we know what signals define a habitat, we can extrapolate to cover
          the entire area. This strategy brings high value from even a small amount
          of field work, reducing the field work needed to cover the entire concession.
          <strong>It is the fastest and most economical way to monitor habitat.</strong>
        </div>
      </div>`;

    root.appendChild(this.panel);
  }

  onEnter(): void {
    if (!this.built) {
      this.build();
      this.built = true;
    }
    this.diffRadial.showDiff(this.diff, this.buildHoverLabels());
    (this.map as unknown as MapLibreAdapter).addRasterSource(COG_LAYER_ID, TILE_URL, 0.85);

    // Floating legend on the map canvas
    const container = (this.map as unknown as MapLibreAdapter).map.getContainer();
    this.mapLegend = document.createElement('div');
    this.mapLegend.className = 'sm-map-signal-legend';
    this.mapLegend.innerHTML = `
      <div class="sm-map-legend-title">Embedding Signals</div>
      <div class="sm-map-legend-item">
        <div class="sm-map-legend-swatch" style="background:#d44;"></div>A01
      </div>
      <div class="sm-map-legend-item">
        <div class="sm-map-legend-swatch" style="background:#4d4;"></div>A44
      </div>
      <div class="sm-map-legend-item">
        <div class="sm-map-legend-swatch" style="background:#44d;"></div>A25
      </div>`;
    container.appendChild(this.mapLegend);
  }

  onLeave(): void {
    this.diffRadial?.stop();
    (this.map as unknown as MapLibreAdapter).removeRasterSource(COG_LAYER_ID);
    if (this.mapLegend) { this.mapLegend.remove(); this.mapLegend = null; }
  }

  private buildHoverLabels(): string[] {
    return this.diff.map((v, i) => {
      const s   = this.soil.embed[i];
      const m   = this.mopane.embed[i];
      const who = v > 0 ? 'soil higher' : 'mopane higher';
      return `A${String(i).padStart(2,'0')}  soil ${s >= 0 ? '+' : ''}${s.toFixed(3)}  mopane ${m >= 0 ? '+' : ''}${m.toFixed(3)}  diff ${v >= 0 ? '+' : ''}${v.toFixed(3)}  (${who})  — click to map`;
    });
  }

  private build(): void {
    const wrap = this.panel.querySelector<HTMLElement>('#sm-svm-diff-wrap')!;
    this.diffRadial = new RadialChart(wrap, 420, 360);

    this.panel.querySelectorAll<HTMLElement>('.sm-svm-dim-card').forEach(card => {
      const bandEl = card.querySelector<HTMLElement>('.sm-svm-dim-band');
      if (!bandEl) return;
      const match = bandEl.textContent?.match(/A\d{2}/);
      if (!match) return;
      const band = match[0];
      card.style.cursor = 'pointer';
      card.title = `Click to overlay ${band} on the map`;
      card.addEventListener('click', () => {
        this.panel.querySelectorAll<HTMLElement>('.sm-svm-dim-card').forEach(c =>
          c.classList.remove('selected'),
        );
        card.classList.add('selected');
        card.dispatchEvent(
          new CustomEvent<{ band: string }>('sm:bandselect', {
            bubbles: true, composed: true, detail: { band },
          }),
        );
      });
    });
  }
}
